/**
 * LLM-narrated reading of the Field of Play.
 *
 * The deterministic `readField` rule engine still grounds the page (and the
 * tests), but this service hands the field to a model as structured JSON and
 * asks it to narrate the situation back: a short read of what is going on, the
 * tensions it sees, and which standard play addresses each. The model is told
 * the exact play catalog so every suggestion maps to a real move the UI can run.
 *
 * Registry: field_reader — on-demand narrator. Transport: callFeatureLlm.
 * Advisory only. Returns null when no LLM is reachable — the caller falls back
 * to the rule engine, so the feature degrades gracefully offline.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import {
  serializeField,
  readField,
  PLAYS,
  type PlacedToken,
  type Tension,
  type Severity,
} from '../data/fieldOfPlay';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const FIELD_READER_SYSTEM_PROMPT =
  'You are an operating coach reading a structured "field of play". Respond with valid JSON only. ' +
  'Be specific and grounded in the pieces provided — never invent pieces, and only suggest plays from the given list.';

export interface FieldNarration {
  /** Source of the reading, so the UI can label it honestly. */
  source: 'llm';
  /** A short, plain-language read of the whole field. */
  narrative: string;
  /** Neutral observations of state. */
  observations: string[];
  /** Anti-patterns, each ideally mapped to a play the UI can run. */
  tensions: Tension[];
}

const VALID_PLAY_IDS = new Set(PLAYS.map((p) => p.id));
const VALID_SEVERITIES = new Set<Severity>(['info', 'warn', 'risk']);

function buildPrompt(tokens: PlacedToken[]): string {
  const field = serializeField(tokens);
  const grounding = readField(tokens);

  return `You are a sharp operating coach reading a team's "field of play" — a board where business work is placed on a grid.

The horizontal axis is the progression of coordinated action: Signal → Frame → Decide → Commit → Execute → Learn (stageIndex 0..5).
The vertical axis is the locus of concern: Organization → Team → Individual.
Where a piece sits is what it means. A piece's "type" is its class (decision, work, risk, unknown, commitment, constraint, actor, evidence). Overlays carry state: priority, confidence, reversibility, owner.

Here is the field as JSON:
${JSON.stringify(field, null, 2)}

A deterministic rule engine already flagged these tensions (use them as a floor, not a ceiling — you may add, merge, or sharpen, but do not contradict an obvious one):
${grounding.tensions.length ? grounding.tensions.map((t) => `- [${t.severity}] ${t.message} (play: ${t.playId ?? 'none'})`).join('\n') : '- (none)'}

The only plays you may suggest, by id:
${PLAYS.map((p) => `- ${p.id}: ${p.name} — ${p.trigger}`).join('\n')}

Your job: narrate the field back. Be specific — name the actual pieces by their label. Reference real positions (e.g. "a decision sitting in Execute"). Do not invent pieces that are not on the field.

Return JSON only, no markdown:
{
  "narrative": "2-4 sentences reading the whole field: what is going on, what is healthy, what is at risk.",
  "observations": ["short neutral facts about the state, 1-4 of them"],
  "tensions": [
    {
      "severity": "info" | "warn" | "risk",
      "message": "one specific sentence naming the piece(s) and the problem",
      "playId": "one play id from the list above, or omit if none fits",
      "tokenIds": ["ids of the implicated pieces from the JSON"]
    }
  ]
}`;
}

function parse(content: string): FieldNarration | null {
  try {
    const match = content.match(/\{[\s\S]*\}/);
    if (!match) return null;
    const parsed = JSON.parse(match[0]) as Record<string, unknown>;

    const narrative = typeof parsed.narrative === 'string' ? parsed.narrative.trim() : '';
    if (!narrative) return null;

    const observations = Array.isArray(parsed.observations)
      ? (parsed.observations as unknown[]).filter((o): o is string => typeof o === 'string' && o.trim().length > 0).slice(0, 6)
      : [];

    const tensions: Tension[] = Array.isArray(parsed.tensions)
      ? (parsed.tensions as unknown[])
          .filter((t): t is Record<string, unknown> => !!t && typeof t === 'object' && typeof (t as Record<string, unknown>).message === 'string')
          .slice(0, 8)
          .map((t, i) => {
            const playId = typeof t.playId === 'string' && VALID_PLAY_IDS.has(t.playId) ? t.playId : undefined;
            const severity = VALID_SEVERITIES.has(t.severity as Severity) ? (t.severity as Severity) : 'warn';
            const tokenIds = Array.isArray(t.tokenIds)
              ? (t.tokenIds as unknown[]).filter((x): x is string => typeof x === 'string')
              : [];
            return { id: `llm-${i}`, severity, message: String(t.message).trim(), playId, tokenIds };
          })
      : [];

    return { source: 'llm', narrative, observations, tensions };
  } catch {
    return null;
  }
}

/**
 * Narrate a field with an LLM. Returns null when LLM is disabled, the field is
 * empty, or no provider is reachable — the caller should fall back to the rule
 * engine and label the reading accordingly.
 */
export async function narrateField(tokens: PlacedToken[]): Promise<FieldNarration | null> {
  if (!isAiCapabilityEnabled('field_reader') || tokens.length === 0) return null;

  try {
    const content = await callFeatureLlm({
      userPrompt: buildPrompt(tokens),
      systemPrompt: FIELD_READER_SYSTEM_PROMPT,
      conversationId: `field-of-play-${Date.now()}`,
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 900,
    });
    if (!content) return null;
    return parse(content);
  } catch (error) {
    console.warn('[llmFieldReaderService] narration failed:', error);
    return null;
  }
}
